/* eslint-disable no-unused-vars, react-hooks/exhaustive-deps  */
import { useCallback } from 'react';
import ReactTooltip from 'react-tooltip';
import { ITopWalletsResponseItem, enums } from '@kattana-io/common';
import DesktopTable from '../../../molecules/Table/DesktopTable';
import Text from '../../../atoms/Text';
import StatsShort from '../StatsShort';
import { useTableData } from './useTableData';
import { ETableTimeframe, parseTableTimeframe } from '../helpers';
import { toFixed4 } from '../formatters';
import { onWalletScan } from '../../../helpers/addressUtils';
import NumberDisplay from '../../../molecules/NumberDisplay';
import WalletField from '../../../molecules/WalletField';

export const LS_TOP_TRADERS_HIDDEN_COLUMNS_KEY =
  'kattana-simple.top_traders_table_columns_hidden';
export const LS_TOP_TRADERS_KEY = 'kattana-simple.top_traders_table_columns';

const sellClassName = 'row--Sell text--red-600';
const buyClassName = 'row--Buy text--green-600';
const bothClassName = 'row--Both text--grey-600';
const myTrade = 'row--Active';

export const getTopTraderPnL = (avgBuy: number, avgSell: number) => {
  if (avgBuy && avgSell) {
    return (100 * (avgSell - avgBuy)) / avgBuy;
  }
  return 0;
};

interface ITopTradersTableProps {
  exchange: string;
  chain: string;
  timeframe: ETableTimeframe;
  loading: boolean;
  address: string | undefined;
  width?: number;
  wallets: any[];
  flipped: boolean;
  plan: enums.PricingPlan;
  showWatchlist: boolean;
  timezone?: string;
  currentWallet?: string;
  onTradeClick(topWallet: ITopWalletsResponseItem): void;
  openWalletModal(props: any): void;
  handleSetTimeframe(day: ETableTimeframe): void;
}

/**
 * Implements a table with trades
 * @param param0
 */
const TopTradersTable = ({
  currentWallet,
  wallets,
  flipped,
  plan,
  timezone,
  timeframe,
  loading,
  exchange,
  address,
  chain,
  showWatchlist,
  width,
  onTradeClick,
  openWalletModal,
  handleSetTimeframe,
}: ITopTradersTableProps) => {
  const showModal = (rowData: ITopWalletsResponseItem) =>
    openWalletModal({
      wallet: rowData?.wallet,
      address,
      exchange,
      timeframe: parseTableTimeframe(timeframe),
      chain,
      timezone,
    });

  const { columns, data, tableColumnsWidth } = useTableData({
    wallets,
  });

  const renderCell = (cell: any) => {
    const { avg_usd_sell, avg_usd_buy } = cell.row.original;

    switch (cell.column.id) {
    case '№':
      return <Text size={12}>{cell.value}</Text>;
    case 'wallet':
      return (
        <WalletField
          textProps={{
            size: 12,
            className: 'tx',
            onClick: () => onWalletScan(cell.value, chain),
          }}
          wallet={cell.value}
          network={chain}
        />
      );
    case 'volumUSD':
      return <Text size={12}>{toFixed4(cell.value)}</Text>;
    case 'avgUsdBuy':
    case 'avgUsdSell':
      return (
        <NumberDisplay
          value={cell.value}
          zeroValueDisplay='-'
          textProps={{ size: 12 }}
        />
      );
    case 'pnl':
      return (
        <NumberDisplay
          value={getTopTraderPnL(avg_usd_buy, avg_usd_sell)}
          endSymbol='%'
          zeroValueDisplay='-'
          textProps={{ size: 12 }}
        />
      );
    case 'statistics':
      return (
        <StatsShort
          walletAddress={cell.value}
          buyCount={cell.row.original.buy_count}
          sellCount={cell.row.original.sell_count}
          bot={!!cell.row.original.bot_count}
          chain={chain}
          showWatchlist={showWatchlist}
          onShowModal={() => {
            showModal(cell.row.original);
          }}
        />
      );
    default:
      return <Text size={12}>{cell.value}</Text>;
    }
  };

  const getClassNameForRow = useCallback(
    (row: ITopWalletsResponseItem) => {
      const classNames: string[] = [];
      if (row.wallet === currentWallet) {
        classNames.push(myTrade);
      }
      if (row.buy_count > 0 && row.sell_count > 0) {
        classNames.push(bothClassName);
      } else {
        if (row.buy_count > 0) {
          if (flipped) {
            classNames.push(sellClassName);
          } else {
            classNames.push(buyClassName);
          }
        } else {
          if (flipped) {
            classNames.push(buyClassName);
          } else {
            classNames.push(sellClassName);
          }
        }
      }
      return classNames.join(' ');
    },
    [flipped, currentWallet]
  );

  const renderTableControls = useCallback(
    () => (
      <>
        <div
          onClick={() => handleSetTimeframe(ETableTimeframe.DAY)}
          className={`pill ${
            timeframe == ETableTimeframe.DAY ? 'pill--active' : ''
          }`}
        >
          1d
        </div>
        <div
          onClick={() =>
            plan === enums.PricingPlan.premium
              ? handleSetTimeframe(ETableTimeframe.WEEK)
              : undefined
          }
          className={`pill ${
            timeframe == ETableTimeframe.WEEK ? 'pill--active' : ''
          } ${plan === enums.PricingPlan.common ? 'pill--disabled' : ''}`}
          data-tip='Available only for premium subscription'
          data-for='top-wallets-timeframes-tooltip'
        >
          7d
        </div>
        <div
          onClick={() =>
            plan === enums.PricingPlan.premium
              ? handleSetTimeframe(ETableTimeframe.MONTH)
              : undefined
          }
          className={`pill ${
            timeframe == ETableTimeframe.MONTH ? 'pill--active' : ''
          } ${plan === enums.PricingPlan.common ? 'pill--disabled' : ''}`}
          data-tip='Available only for premium subscription'
          data-for='top-wallets-timeframes-tooltip'
        >
          1M
        </div>
        {plan === enums.PricingPlan.common && (
          <ReactTooltip
            id='top-wallets-timeframes-tooltip'
            place='bottom'
            effect='solid'
            className='text_tooltip'
          />
        )}
      </>
    ),
    [timeframe, plan]
  );

  return (
    <DesktopTable
      hiddenColumnsKey={LS_TOP_TRADERS_HIDDEN_COLUMNS_KEY}
      dataKey={LS_TOP_TRADERS_KEY}
      title='top_traders'
      tableColumnsWidth={tableColumnsWidth}
      tableWidth={width || 0}
      columns={columns}
      data={data}
      hiddenColumns={[]}
      loading={loading}
      getClassNameForRow={getClassNameForRow}
      renderCell={renderCell}
      renderTableControls={renderTableControls}
      onRowClick={onTradeClick}
    />
  );
};

export default TopTradersTable;
